import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Clock, AlertCircle, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const maxAttempts = 8;
    const pollInterval = 2000;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setStatus("timeout");
        return;
      }
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        setOrderId(res.data.order_id);
        if (res.data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (res.data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts++;
        setTimeout(poll, pollInterval);
      } catch {
        attempts++;
        setTimeout(poll, pollInterval);
      }
    };

    poll();
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps -- poll is self-contained with local attempts counter

  return (
    <main data-testid="checkout-success-page" className="pt-20 min-h-screen flex items-center">
      <div className="max-w-lg mx-auto px-6 py-20 text-center">
        {status === "loading" && (
          <div data-testid="payment-loading">
            <Loader2 size={48} className="mx-auto mb-6 text-[#0D2D54] animate-spin" />
            <h1 className="font-['Outfit'] text-2xl font-medium text-[#0D2D54] mb-3">
              Processing your payment...
            </h1>
            <p className="text-sm text-[#6B7280]">
              Please wait while we confirm your payment.
            </p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="payment-success">
            <CheckCircle size={48} className="mx-auto mb-6 text-[#758C7B]" />
            <h1 className="font-['Outfit'] text-2xl font-medium text-[#0D2D54] mb-3">
              Payment Successful
            </h1>
            <p className="text-sm text-[#6B7280] mb-2">
              Your order has been confirmed. Our team will begin staging your images within the next 48 hours.
            </p>
            {orderId && (
              <p className="text-xs text-[#6B7280] mb-8">
                Order ID: <span className="font-mono">{orderId}</span>
              </p>
            )}
            <Link
              to="/"
              data-testid="back-home-btn"
              className="inline-flex items-center gap-2 bg-[#0D2D54] text-white px-8 py-3 text-sm font-medium tracking-wide hover:bg-[#1A3D6B] transition-all"
            >
              Back to Home
            </Link>
          </div>
        )}

        {status === "timeout" && (
          <div data-testid="payment-timeout">
            <Clock size={48} className="mx-auto mb-6 text-[#758C7B]" />
            <h1 className="font-['Outfit'] text-2xl font-medium text-[#0D2D54] mb-3">
              Payment Processing
            </h1>
            <p className="text-sm text-[#6B7280] mb-8">
              Your payment is still being processed. You'll receive a confirmation email once it's complete.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-[#0D2D54] text-white px-8 py-3 text-sm font-medium tracking-wide hover:bg-[#1A3D6B] transition-all"
            >
              Back to Home
            </Link>
          </div>
        )}

        {(status === "error" || status === "expired") && (
          <div data-testid="payment-error">
            <AlertCircle size={48} className="mx-auto mb-6 text-red-500" />
            <h1 className="font-['Outfit'] text-2xl font-medium text-[#0D2D54] mb-3">
              {status === "expired" ? "Session Expired" : "Something Went Wrong"}
            </h1>
            <p className="text-sm text-[#6B7280] mb-8">
              {status === "expired"
                ? "Your payment session has expired. Please try again."
                : "We couldn't process your payment. Please try again."}
            </p>
            <Link
              to="/order"
              className="inline-flex items-center gap-2 bg-[#0D2D54] text-white px-8 py-3 text-sm font-medium tracking-wide hover:bg-[#1A3D6B] transition-all"
            >
              Try Again
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}
